import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Download, Filter, X, Loader2, AlertCircle, ChevronDown, Settings, Plus, Eye, EyeOff, Columns } from 'lucide-react';

const ArtworkExportDashboard = () => {
  // State
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showColumnManager, setShowColumnManager] = useState(false);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    sku: true,
    title: true,
    status_name: true,
    artgalleria_id: true,
    shopify_id: true,
    created_at: false,
    updated_at: false
  });

  // Selected properties to show as columns
  const [selectedProperties, setSelectedProperties] = useState([]);

  // Property filters - each property can have its own filter
  const [propertyFilters, setPropertyFilters] = useState({});

  // Filter state
  const [filters, setFilters] = useState({
    status_names: [],
    exclude_statuses: [],
    sku_filter: '',
    title_filter: '',
    has_shopify_id: null
  });

  // UI state
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showExcludeDropdown, setShowExcludeDropdown] = useState(false);

  const observerTarget = useRef(null);
  const API_BASE_URL = 'https://api.synchronizer.iconicartsystem.com/info/artworks';
  const ITEMS_PER_PAGE = 50;

  // Base columns definition
  const baseColumns = [
    { key: 'sku', label: 'SKU', sortable: true },
    { key: 'title', label: 'Title', sortable: true },
    { key: 'status_name', label: 'Status', sortable: true },
    { key: 'artgalleria_id', label: 'Artgalleria ID', sortable: true },
    { key: 'shopify_id', label: 'Shopify ID', sortable: true },
    { key: 'created_at', label: 'Created', sortable: true },
    { key: 'updated_at', label: 'Updated', sortable: true }
  ];

  // Fetch available options on mount
  useEffect(() => {
    fetchAvailableOptions();
  }, []);

  const fetchAvailableOptions = async () => {
    try {
      const [statusesRes, propertiesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/export/statuses`),
        fetch(`${API_BASE_URL}/export/properties`)
      ]);

      if (statusesRes.ok) {
        const statusData = await statusesRes.json();
        setAvailableStatuses(statusData.statuses || []);
      }

      if (propertiesRes.ok) {
        const propData = await propertiesRes.json();
        setAvailableProperties(propData.properties || []);
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  };

  // Build query string from filters
  const buildQueryString = useCallback((pageNum = 1) => {
    const params = new URLSearchParams();
    
    if (filters.status_names.length > 0) {
      params.append('status_names', filters.status_names.join(','));
    }
    if (filters.exclude_statuses.length > 0) {
      params.append('exclude_statuses', filters.exclude_statuses.join(','));
    }
    if (filters.sku_filter.trim()) {
      params.append('sku_filter', filters.sku_filter.trim());
    }
    if (filters.title_filter.trim()) {
      params.append('title_filter', filters.title_filter.trim());
    }
    if (filters.has_shopify_id !== null) {
      params.append('has_shopify_id', filters.has_shopify_id);
    }

    // Add selected properties
    if (selectedProperties.length > 0) {
      params.append('property_names', selectedProperties.join(','));
    }

    params.append('page', pageNum);
    params.append('page_size', ITEMS_PER_PAGE);
    params.append('include_properties', 'true');
    params.append('include_images', 'false');

    return params.toString();
  }, [filters, selectedProperties]);

  // Fetch artworks
  const fetchArtworks = async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const queryString = buildQueryString(pageNum);
      const url = `${API_BASE_URL}/export/list?${queryString}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Apply client-side property filters
      let filteredData = data.data;
      Object.keys(propertyFilters).forEach(propName => {
        const filterValue = propertyFilters[propName];
        if (filterValue && filterValue.trim()) {
          filteredData = filteredData.filter(artwork => {
            const propValue = artwork.properties?.[propName];
            return propValue && String(propValue).toLowerCase().includes(filterValue.toLowerCase());
          });
        }
      });
      
      if (append) {
        setArtworks(prev => [...prev, ...filteredData]);
      } else {
        setArtworks(filteredData);
      }
      
      setTotalCount(data.pagination.total_items);
      setHasMore(data.pagination.has_next);
    } catch (err) {
      console.error('Error fetching artworks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load and filter changes
  useEffect(() => {
    setPage(1);
    setArtworks([]);
    setHasMore(true);
    fetchArtworks(1, false);
  }, [filters, selectedProperties]);

  // Apply property filters client-side when they change
  useEffect(() => {
    if (artworks.length > 0) {
      setPage(1);
      fetchArtworks(1, false);
    }
  }, [propertyFilters]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchArtworks(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore, page]);

  // Download CSV
  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      if (filters.status_names.length > 0) {
        params.append('status_names', filters.status_names.join(','));
      }
      if (filters.exclude_statuses.length > 0) {
        params.append('exclude_statuses', filters.exclude_statuses.join(','));
      }
      if (filters.sku_filter.trim()) {
        params.append('sku_filter', filters.sku_filter.trim());
      }
      if (filters.title_filter.trim()) {
        params.append('title_filter', filters.title_filter.trim());
      }
      if (filters.has_shopify_id !== null) {
        params.append('has_shopify_id', filters.has_shopify_id);
      }
      if (selectedProperties.length > 0) {
        params.append('property_names', selectedProperties.join(','));
      }

      const url = `${API_BASE_URL}/export/csv?${params.toString()}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `artworks_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Error downloading:', err);
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  // Toggle array filters
  const toggleArrayFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value]
    }));
  };

  // Toggle property selection
  const toggleProperty = (propName) => {
    setSelectedProperties(prev => {
      if (prev.includes(propName)) {
        // Remove property and its filter
        const newFilters = { ...propertyFilters };
        delete newFilters[propName];
        setPropertyFilters(newFilters);
        return prev.filter(p => p !== propName);
      } else {
        return [...prev, propName];
      }
    });
  };

  // Toggle base column visibility
  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status_names: [],
      exclude_statuses: [],
      sku_filter: '',
      title_filter: '',
      has_shopify_id: null
    });
    setPropertyFilters({});
  };

  // Check if any filters are active
  const hasActiveFilters = 
    filters.status_names.length > 0 ||
    filters.exclude_statuses.length > 0 ||
    filters.sku_filter.trim() ||
    filters.title_filter.trim() ||
    filters.has_shopify_id !== null ||
    Object.values(propertyFilters).some(v => v && v.trim());

  // Get visible base columns
  const getVisibleBaseColumns = () => {
    return baseColumns.filter(col => visibleColumns[col.key]);
  };

  // Format cell value
  const formatCellValue = (value, columnKey) => {
    if (!value) return 'N/A';
    
    if (columnKey === 'created_at' || columnKey === 'updated_at') {
      return new Date(value).toLocaleDateString();
    }
    
    return value;
  };

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Fixed Navbar */}
      <nav className="flex-shrink-0 bg-white border-b border-gray-200 z-30 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Title and Stats */}
            <div className="flex items-center gap-6">
              <h1 className="text-2xl font-bold text-gray-900">Artwork Export</h1>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{totalCount.toLocaleString()}</span>
                  <span className="text-gray-600">total</span>
                </div>
                <div className="h-4 w-px bg-gray-300"></div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{artworks.length.toLocaleString()}</span>
                  <span className="text-gray-600">loaded</span>
                </div>
                <div className="h-4 w-px bg-gray-300"></div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-blue-600">{selectedProperties.length}</span>
                  <span className="text-gray-600">properties</span>
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
              <button
                onClick={() => {
                  setShowColumnManager(!showColumnManager);
                  setShowFilterPanel(false);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showColumnManager 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Columns className="w-4 h-4" />
                Columns
                <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-white bg-opacity-30 rounded-full">
                  {getVisibleBaseColumns().length + selectedProperties.length}
                </span>
              </button>
              <button
                onClick={() => {
                  setShowFilterPanel(!showFilterPanel);
                  setShowColumnManager(false);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showFilterPanel 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-white bg-opacity-30 rounded-full">
                    {[
                      filters.status_names.length,
                      filters.exclude_statuses.length,
                      filters.sku_filter ? 1 : 0,
                      filters.title_filter ? 1 : 0,
                      filters.has_shopify_id !== null ? 1 : 0,
                      Object.values(propertyFilters).filter(v => v && v.trim()).length
                    ].reduce((a, b) => a + b, 0)}
                  </span>
                )}
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading || loading}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download CSV
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Column Manager Panel */}
      {showColumnManager && (
        <div className="flex-shrink-0 bg-white border-b border-gray-200 z-20 shadow-lg">
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Base Columns */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Base Columns</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {baseColumns.map(col => (
                    <label
                      key={col.key}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumnVisibility(col.key)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Property Columns */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Property Columns
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    ({selectedProperties.length} selected)
                  </span>
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableProperties.map(prop => (
                    <label
                      key={prop}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProperties.includes(prop)}
                        onChange={() => toggleProperty(prop)}
                        className="rounded text-blue-600"
                      />
                      <span className="text-sm text-gray-700">{prop}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Panel */}
      {showFilterPanel && (
        <div className="flex-shrink-0 bg-white border-b border-gray-200 z-20 shadow-lg max-h-96 overflow-y-auto">
          <div className="px-6 py-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Global Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              {/* Status Names Filter */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Include Status
                </label>
                <button
                  onClick={() => {
                    setShowStatusDropdown(!showStatusDropdown);
                    setShowExcludeDropdown(false);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-sm"
                >
                  <span>
                    {filters.status_names.length > 0 
                      ? `${filters.status_names.length} selected`
                      : 'All statuses'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                {showStatusDropdown && (
                  <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {availableStatuses.map(status => (
                      <label
                        key={status}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.status_names.includes(status)}
                          onChange={() => toggleArrayFilter('status_names', status)}
                          className="rounded"
                        />
                        <span className="text-sm">{status}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Exclude Status Filter */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Exclude Status
                </label>
                <button
                  onClick={() => {
                    setShowExcludeDropdown(!showExcludeDropdown);
                    setShowStatusDropdown(false);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-sm"
                >
                  <span>
                    {filters.exclude_statuses.length > 0 
                      ? `${filters.exclude_statuses.length} excluded`
                      : 'None excluded'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                {showExcludeDropdown && (
                  <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {availableStatuses.map(status => (
                      <label
                        key={status}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.exclude_statuses.includes(status)}
                          onChange={() => toggleArrayFilter('exclude_statuses', status)}
                          className="rounded"
                        />
                        <span className="text-sm">{status}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* SKU Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  SKU
                </label>
                <input
                  type="text"
                  placeholder="Search SKU..."
                  value={filters.sku_filter}
                  onChange={(e) => setFilters(prev => ({ ...prev, sku_filter: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Title Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Title
                </label>
                <input
                  type="text"
                  placeholder="Search title..."
                  value={filters.title_filter}
                  onChange={(e) => setFilters(prev => ({ ...prev, title_filter: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Shopify ID Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Shopify
                </label>
                <select
                  value={filters.has_shopify_id === null ? '' : filters.has_shopify_id}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    has_shopify_id: e.target.value === '' ? null : e.target.value === 'true'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">All</option>
                  <option value="true">With ID</option>
                  <option value="false">Without ID</option>
                </select>
              </div>
            </div>

            {/* Property Filters */}
            {selectedProperties.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Property Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {selectedProperties.map(propName => (
                    <div key={propName}>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        {propName}
                      </label>
                      <input
                        type="text"
                        placeholder={`Filter ${propName}...`}
                        value={propertyFilters[propName] || ''}
                        onChange={(e) => setPropertyFilters(prev => ({
                          ...prev,
                          [propName]: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">Error</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Artworks Table */}
        {loading && artworks.length === 0 ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : artworks.length > 0 ? (
          <div className="px-6 py-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      {getVisibleBaseColumns().map(col => (
                        <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {col.label}
                        </th>
                      ))}
                      {selectedProperties.map(propName => (
                        <th key={propName} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="truncate max-w-[200px]">{propName}</span>
                            {propertyFilters[propName] && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                filtered
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {artworks.map((artwork) => (
                      <tr key={artwork.id} className="hover:bg-gray-50 transition-colors">
                        {getVisibleBaseColumns().map(col => (
                          <td key={col.key} className="px-6 py-4 whitespace-nowrap">
                            {col.key === 'status_name' ? (
                              <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                                {artwork[col.key]}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-900">
                                {formatCellValue(artwork[col.key], col.key)}
                              </span>
                            )}
                          </td>
                        ))}
                        {selectedProperties.map(propName => (
                          <td key={propName} className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-700">
                              {artwork.properties?.[propName] || 'N/A'}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                <span className="ml-3 text-sm text-gray-600">Loading more artworks...</span>
              </div>
            )}

            {/* End of Results */}
            {!hasMore && artworks.length > 0 && (
              <div className="text-center py-8 text-sm text-gray-600">
                All {totalCount.toLocaleString()} artworks loaded
              </div>
            )}

            {/* Intersection Observer Target */}
            <div ref={observerTarget} className="h-4"></div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32">
            <Search className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No artworks found</h3>
            <p className="text-gray-600">Try adjusting your filters or column selection</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtworkExportDashboard;